import { uniq } from "lodash";
import { createSelector } from "reselect";

import { TicketType } from "@busbud/int-schemas/udm";

import { isAnOperatorAwardAnnotationId } from "@app/helpers/operator-awards";
import { getDisplayPrice } from "@app/helpers/udm/get-display-price";
import { getTripTicketTypes } from "@app/helpers/udm/get-trip-ticket-types";
import {
  getSearchedLeg,
  getSegmentsFromTripLeg,
  isDirectLeg
} from "@app/helpers/udm/index";
import { getDepartureTimeRange } from "@app/modules/results/helpers/departure-date-range";
import { getNormalizedClassName } from "@app/modules/results/utils/classes/normalized-class-name";
import { getDepartureTime } from "@app/tracking/helpers/udm";

import { getFilteredLegCardIdsByExcludedSources } from "./get-filtered-leg-card-ids-by-excluded-sources";
import { getLegCards } from "./get-leg-cards";
import { getDisplayedPriceConfig } from "../get-displayed-price-config";
import { getPointsOfInterest } from "../get-points-of-interest";
import {
  getSearch,
  getTrips,
  getTripsOperators,
  getTripsSegments
} from "../state-accessors";

export const getDataForCombinedFilters = createSelector(
  [
    getLegCards,
    getFilteredLegCardIdsByExcludedSources,
    getTrips,
    getTripsSegments,
    getSearch,
    getTripsOperators,
    getPointsOfInterest,
    getDisplayedPriceConfig
  ],
  (
    leg_cards,
    filtered_leg_cards_by_excluded_sources,
    trips,
    segments,
    search,
    operators,
    points_of_interest,
    displayed_price_config
  ) => {
    // insure we feed combined filters with the correct and updated data so filters react correctly
    let leg_card_ids = Object.keys(leg_cards);

    if (filtered_leg_cards_by_excluded_sources.length) {
      leg_card_ids = leg_card_ids.filter(id =>
        filtered_leg_cards_by_excluded_sources.includes(id)
      );
    }

    return leg_card_ids.map(id => {
      // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const trip = trips[leg_cards[id][0]];
      const leg = getSearchedLeg(search, trip);
      const leg_segments = getSegmentsFromTripLeg(leg, segments);
      const first_segment = leg_segments[0];
      const price = getDisplayPrice(trip, "trip", displayed_price_config);
      const departure_time = getDepartureTimeRange(
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        getDepartureTime(first_segment)
      );
      const trip_segments = getSegmentsFromTripLeg(leg, segments);
      // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const vehicle_type = trip_segments[0].vehicle.type;

      const operator_ids = uniq(
        trip_segments.map(segment => segment.operator_id)
      );

      const profile_ids = uniq(
        operator_ids
          .map(operator_id => operators[operator_id]?.profile_id)
          .filter(profile_id => !!profile_id)
      ) as number[];

      const is_direct_only = isDirectLeg(leg);
      const has_e_ticket = getTripTicketTypes(leg_segments).includes(
        TicketType.ETicket
      );

      // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const origin = trip_segments[0].origin_id;
      const destination =
        // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        trip_segments[trip_segments.length - 1].destination_id;

      // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const classes: string[] = leg_cards[id].map(trip_id => {
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        const leg = getSearchedLeg(search, trips[trip_id]);
        const first_segment_id = leg.segment_ids[0];
        //@ts-expect-error UDMV4 migration
        return getNormalizedClassName(segments[first_segment_id].class);
      });

      const is_award_winner = operator_ids.some(operator_id =>
        operators[operator_id]?.annotations?.some(annotation =>
          isAnOperatorAwardAnnotationId(annotation)
        )
      );

      const points_of_interest_values = Object.values(points_of_interest);

      const origin_points_of_interest = points_of_interest_values.filter(
        ({ location_ids }) => location_ids.includes(origin)
      );
      const origin_points_of_interest_ids = origin_points_of_interest.map(
        ({ uuid }) => uuid
      );

      const destination_points_of_interest = points_of_interest_values.filter(
        ({ location_ids }) => location_ids.includes(destination)
      );
      const destination_points_of_interest_ids =
        destination_points_of_interest.map(({ uuid }) => uuid);

      return {
        id,
        departure_time,
        price,
        vehicle_type,
        operator_ids,
        profile_ids,
        is_direct_only,
        has_e_ticket,
        // origin,
        // destination,
        is_award_winner,
        classes,
        origin_poi: origin_points_of_interest_ids,
        destination_poi: destination_points_of_interest_ids
      };
    });
  }
);
